import { useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import {
  DIAL_CODES,
  dialLabel,
  draftOf,
  switchDial,
  typeDraft,
  type DialCode,
  type PhoneMode,
} from '../lib/dial-codes';

/**
 * 「区号 + 号码」两段式电话输入。
 *
 * 一个框里手打 `+86 138 0013 8000` 的错法都很安静：漏 `+`、多空格、
 * 写成 `0086` —— 客户自己都看不出来，结果就是页脚一个拨不通的号码、
 * 结构化数据里一个搜不到的 telephone。拆成两段之后，区号由下拉框保证，
 * 号码框只收数字和分隔符，这类错误在结构上就不成立了。
 *
 * 两个消费方共用同一个组件，但落库形态不同（见 `joinDial`）：
 *   · 电话 → `display`，保留空格分组，页脚原样显示（`+86 574 8712 3456`）
 *   · WhatsApp → `digits`，只留数字，`wa.me` 只认这个（`8613800138000`）
 *
 * **编辑态是组件自己的 state，不从 value 反解**。反解见 `draftOf` 上那段注释：
 * 逐字输入时区号会被当成号码的前两位显示出来（"一输入就变成 86"）。
 */
export function DialPhoneField({
  label,
  value,
  onChange,
  mode = 'display',
  placeholder,
  children,
}: {
  label: string;
  value: string | null;
  onChange: (value: string | null) => void;
  mode?: PhoneMode;
  placeholder?: string;
  /** 号码框下面那一行：链接预览或校验提示由调用方给，组件不猜 */
  children?: ReactNode;
}) {
  /**
   * 客户眼下的两个框。区号要单独存的状态理由见下；号码同理 ——
   * 反解会在号码还不完整时把区号漏进号码框。
   */
  const [draft, setDraft] = useState(() => draftOf(value));

  /**
   * 我们自己最后一次抛出去的值。
   *
   * 用来区分「外部换值」和「自己编辑造成的回环」：每次输入都会先 onChange、
   * 再由父组件把新 value 传回来，如果照单全收地反解一遍，客户正在敲的字会被
   * 覆盖（选了英国 +44、号码空着时会被弹回中国 +86 —— 表现为下拉框选不动）。
   */
  const emitted = useRef<string | null | undefined>(undefined);

  useEffect(() => {
    if (value === emitted.current) return;
    setDraft(draftOf(value));
  }, [value]);

  /** 落库值只有一个出口，免得两处调用忘了同步 `emitted` */
  const commit = (next: { draft: { dial: string; local: string }; value: string | null }) => {
    setDraft(next.draft);
    emitted.current = next.value;
    onChange(next.value);
  };

  /** 表外区号（`+381` 之类）临时补一项，否则下拉框会显示成空白 */
  const options = useMemo<DialCode[]>(() => {
    if (draft.dial === '' || DIAL_CODES.some((item) => item.code === draft.dial)) {
      return [...DIAL_CODES];
    }
    return [...DIAL_CODES, { code: draft.dial, name: '' }];
  }, [draft.dial]);

  return (
    <div className="field-block">
      <span>{label}</span>
      <div className="dial-row">
        <select
          className="field dial-row__code"
          aria-label={`${label}国家码`}
          value={draft.dial}
          onChange={(e) => commit(switchDial(draft, e.target.value, mode))}
        >
          {options.map((item) => (
            <option key={item.code} value={item.code}>
              {dialLabel(item)}
            </option>
          ))}
        </select>
        <input
          className="field dial-row__number"
          autoComplete="off"
          inputMode="tel"
          placeholder={placeholder}
          aria-label={label}
          value={draft.local}
          onChange={(e) => {
            const raw = e.target.value;
            /**
             * 一次进来不止一个字 = 粘贴（或输入法一次上屏一整串）。
             *
             * 只有这时才允许剥掉整串里的国家码 —— 逐字敲时客户号码本身
             * 就可能以 86 打头，剥了就是"少了一位"。
             */
            const inputType = (e.nativeEvent as InputEvent | undefined)?.inputType ?? '';
            // 一次变长不止一个字 = 一次进来一整串。只认"变长"：删字、替换选中项
            // 都会让文本变短，那不是粘贴
            const pasted = inputType.startsWith('insertFrom') || raw.length - draft.local.length > 1;
            commit(typeDraft(draft, raw, mode, pasted));
          }}
        />
      </div>
      {children}
    </div>
  );
}
